import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { HeadUpdateResult, HeadsResult } from './types'
import type { WorkspaceAuthProvider } from './workspaceSecretV2'
import { recordBrowserHttpErrorResponse } from './browserHttpErrors'
import { sdkConsole } from './logger'

// ── Interfaces ─────────────────────────────────────────────────

export interface StoreBackend {
    load(hash: string): Promise<Uint8Array | null>
    store(hash: string, data: Uint8Array): Promise<void>
    hasObject?(hash: string): Promise<boolean>
    findMissingObjects?(hashes: string[]): Promise<string[]>

    // Batch operations
    loadBatch(hashes: string[]): Promise<Map<string, Uint8Array>>
    storeBatch(entries: Array<{ hash: string; data: Uint8Array }>): Promise<void>

    // Outbox persistence (optional)
    loadOutbox?(blobId: string): Promise<Array<{ hash: string; data: Uint8Array }>>
    storeOutbox?(blobId: string, hash: string, data: Uint8Array): Promise<void>
    clearOutbox?(blobId: string, hashes: string[]): Promise<void>

    // Local head management (optional)
    getLocalHeads?(blobId: string): Promise<string[]>
    setLocalHeads?(blobId: string, heads: string[]): Promise<void>
    getRemoteHead?(blobId: string): Promise<string | undefined>
    setRemoteHead?(blobId: string, head: string): Promise<void>
    /** T1: Atomic local+remote head update in a single transaction */
    setHeadsAtomically?(blobId: string, opts: { local?: string[]; remote?: string }): Promise<void>
}

export interface SyncBackend {
    getHeads(blobId: string, capability?: string, lane?: string): Promise<HeadsResult>
    updateHead(blobId: string, base: string[], newHead: string, capability?: string, referencedObjects?: string[], prefetchHints?: string[], leaseId?: string, releaseLeaseId?: string, lane?: string): Promise<HeadUpdateResult>
    softDeleteBlob?(blobId: string, capability?: string): Promise<void>

    // Lease API (§94 §7) — resource-scoped, optional, implemented by HttpStoreBackend
    acquireLease?(blobId: string, resourceId: string, mode: number, writerKid: string, ttlSeconds?: number): Promise<{ leaseId: string; mode: number; expiresAt: string; resourceId: string } | null>
    refreshLease?(blobId: string, leaseId: string, ttlSeconds?: number): Promise<{ leaseId: string; expiresAt: string; resourceId?: string; mode?: number } | null>
    releaseLease?(blobId: string, leaseId: string): Promise<boolean>
}

const CONTENT_HASH_HEADER_NAME = 'x-secureblob-content-sha256'
const ATTRIBUTION_CLIENT_HEADER = 'x-secureblob-client'
const ATTRIBUTION_WORKLOAD_HEADER = 'x-secureblob-workload'
const ATTRIBUTION_WORKSPACE_TYPE_HEADER = 'x-secureblob-workspace-type'

function toStandaloneArrayBufferView(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
    if (bytes.buffer instanceof ArrayBuffer && bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength) {
        return bytes as Uint8Array<ArrayBuffer>
    }
    return new Uint8Array(bytes) as Uint8Array<ArrayBuffer>
}

function bytesToHex(bytes: Uint8Array): string {
    let hex = ''
    for (const byte of bytes) hex += byte.toString(16).padStart(2, '0')
    return hex
}

async function computeSha256Hex(bytes: Uint8Array): Promise<string> {
    return bytesToHex(nobleSha256(toStandaloneArrayBufferView(bytes)))
}

// ── Helpers ────────────────────────────────────────────────────

function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
}

type WorkerGlobalScopeConstructor = abstract new (...args: never[]) => object

/**
 * Ephemeral storage header for E2E tests.
 * Playwright fixtures inject `window.__e2eStorageSecret` via addInitScript,
 * which persists across navigations. When present, all API requests include
 * `X-Storage-Mode: memory:<secret>` so the server routes to in-memory storage.
 */
function getEphemeralHeaders(): Record<string, string> {
    const secret = typeof window !== 'undefined' && (window as any).__e2eStorageSecret
    if (secret) {
        return { 'X-Storage-Mode': `memory:${secret}` }
    }
    return {}
}

import { EngineException, type EngineError, type EngineErrorCode } from './types'

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<string>([
    'NETWORK_ERROR', 'CAS_MISMATCH', 'FORBIDDEN', 'CORRUPT_DATA',
    'ABORTED', 'NOT_FOUND', 'COMMIT_INVALID', 'BLOB_SOFT_DELETED', 'OBJECT_MISSING',
])
const DEFAULT_OBJECT_REQUEST_TIMEOUT_MS = 120_000
const DEFAULT_RESPONSE_BODY_TIMEOUT_MS = 120_000
const DEFAULT_IMMUTABLE_OBJECT_REQUEST_TIMEOUT_MS = 120_000
const DEFAULT_IMMUTABLE_OBJECT_RESPONSE_BODY_TIMEOUT_MS = 120_000
const DEFAULT_BATCH_PUT_REQUEST_TIMEOUT_MS = 300_000
const DEFAULT_HEAD_REQUEST_TIMEOUT_MS = 300_000
const MAX_CONFIGURED_TIMEOUT_MS = 1_800_000
const SUCCESS_RESPONSE_BODY_DRAIN_TIMEOUT_MS = 250
const IMMUTABLE_OBJECT_MAX_ATTEMPTS = 3
const RESPONSE_CANCEL_TIMEOUT_MS = 250
const ABORT_FETCH_ON_TIMEOUT_ENV = 'SECUREBLOB_SQLITE3MT_ABORT_FETCH_ON_TIMEOUT'
const RETRYABLE_HTTP_MAX_ATTEMPTS = 20
const RETRYABLE_HTTP_BACKOFF_MS = [
    2_000,
    10_000,
    20_000,
    30_000,
    40_000,
    60_000,
]
let httpRequestSeq = 0
const HTTP_REQUEST_ID = Symbol('sqlite3mt-http-request-id')

function readNodeEnv(name: string): string | undefined {
    const processLike = (globalThis as typeof globalThis & {
        __secureblobComponentJobRuntimeEnv?: Record<string, string | undefined>
        process?: { env?: Record<string, string | undefined> }
    })
    const runtimeValue = processLike.__secureblobComponentJobRuntimeEnv?.[name]
    if (typeof runtimeValue === 'string') {
        return runtimeValue
    }
    const processEnv = processLike.process
    return processEnv?.env?.[name]
}

function readConfiguredTimeoutMs(name: string, fallback: number): number {
    const value = readNodeEnv(name)
    if (value == null || value.trim().length === 0) {
        return fallback
    }

    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= MAX_CONFIGURED_TIMEOUT_MS) {
        return Math.trunc(parsed)
    }

    sdkConsole.warn('[secureblob:http] ignoring invalid timeout override', {
        name,
        value,
        fallback,
    })
    return fallback
}

function objectRequestTimeoutMs(): number {
    return readConfiguredTimeoutMs('SECUREBLOB_SQLITE3MT_OBJECT_REQUEST_TIMEOUT_MS', DEFAULT_OBJECT_REQUEST_TIMEOUT_MS)
}

function responseBodyTimeoutMs(): number {
    return readConfiguredTimeoutMs('SECUREBLOB_SQLITE3MT_RESPONSE_BODY_TIMEOUT_MS', DEFAULT_RESPONSE_BODY_TIMEOUT_MS)
}

function immutableObjectRequestTimeoutMs(): number {
    return readConfiguredTimeoutMs('SECUREBLOB_SQLITE3MT_IMMUTABLE_OBJECT_REQUEST_TIMEOUT_MS', DEFAULT_IMMUTABLE_OBJECT_REQUEST_TIMEOUT_MS)
}

function immutableObjectResponseBodyTimeoutMs(): number {
    return readConfiguredTimeoutMs('SECUREBLOB_SQLITE3MT_IMMUTABLE_OBJECT_RESPONSE_BODY_TIMEOUT_MS', DEFAULT_IMMUTABLE_OBJECT_RESPONSE_BODY_TIMEOUT_MS)
}

function batchPutRequestTimeoutMs(): number {
    return readConfiguredTimeoutMs('SECUREBLOB_SQLITE3MT_BATCH_PUT_REQUEST_TIMEOUT_MS', DEFAULT_BATCH_PUT_REQUEST_TIMEOUT_MS)
}

function headRequestTimeoutMs(): number {
    return readConfiguredTimeoutMs('SECUREBLOB_SQLITE3MT_HEAD_REQUEST_TIMEOUT_MS', DEFAULT_HEAD_REQUEST_TIMEOUT_MS)
}

function isKnownErrorCode(code: string): code is EngineErrorCode {
    return KNOWN_ERROR_CODES.has(code)
}

/**
 * Extract a typed EngineErrorCode from a structured server error response.
 * Expects shape: { error: { code: string, ... } }
 * Returns the server code if recognized, otherwise falls back to the provided default.
 */
function extractServerErrorCode(responseBody: unknown, fallback: EngineErrorCode = 'NETWORK_ERROR'): EngineErrorCode {
    if (responseBody && typeof responseBody === 'object' && 'error' in (responseBody as Record<string, unknown>)) {
        const serverErr = (responseBody as Record<string, unknown>).error as Record<string, unknown> | undefined
        if (serverErr?.code && typeof serverErr.code === 'string' && isKnownErrorCode(serverErr.code)) {
            return serverErr.code
        }
    }
    return fallback
}

function isRequestTimeoutError(error: unknown): boolean {
    return error instanceof Error
        && (
            error.message.startsWith('Request timed out after ')
            || error.message.startsWith('Response body timed out after ')
        )
}

function isBrowserTransportError(error: unknown): boolean {
    return error instanceof Error && error.name === 'TypeError'
}

function isRetryableHttpError(error: unknown, retryBrowserTransportErrors = false): boolean {
    if (error instanceof DOMException && error.name === 'AbortError') {
        return false
    }
    if (error instanceof EngineException) {
        return error.error.retryable
    }
    if (retryBrowserTransportErrors && isBrowserTransportError(error)) {
        return true
    }
    return isRequestTimeoutError(error)
}

function parseRetryAfterMs(value: string | undefined): number | null {
    if (value == null || value.trim().length === 0) {
        return null
    }
    const trimmed = value.trim()
    const seconds = Number(trimmed)
    if (Number.isFinite(seconds) && seconds >= 0) {
        return Math.trunc(seconds * 1_000)
    }

    const retryAtMs = Date.parse(trimmed)
    if (Number.isFinite(retryAtMs)) {
        return Math.max(0, retryAtMs - Date.now())
    }
    return null
}

function retryAfterHeaderMs(error: unknown): number | null {
    if (!(error instanceof EngineException)) {
        return null
    }
    const headers = (error.error.details as { response?: { headers?: Record<string, string> } } | undefined)
        ?.response
        ?.headers
    return parseRetryAfterMs(headers?.['retry-after'] ?? headers?.['Retry-After'])
}

function retryDelayMs(error: unknown, failedAttempt: number): number {
    const configured = RETRYABLE_HTTP_BACKOFF_MS[
        Math.min(failedAttempt - 1, RETRYABLE_HTTP_BACKOFF_MS.length - 1)
    ] ?? RETRYABLE_HTTP_BACKOFF_MS[RETRYABLE_HTTP_BACKOFF_MS.length - 1]!
    return Math.max(configured, retryAfterHeaderMs(error) ?? 0)
}

async function withRetry<T>(
    fn: () => Promise<T>,
    maxAttempts = 1,
    options?: {
        logPrefix?: string
        label?: string
        retryRequestTimeouts?: boolean
        retryBrowserTransportErrors?: boolean
    },
): Promise<T> {
    let lastError: unknown = null
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            return await fn()
        } catch (error) {
            lastError = error
            // §7.8.5: Never retry AbortError — propagate immediately
            if (error instanceof DOMException && error.name === 'AbortError') throw error
            if (options?.retryRequestTimeouts === false && isRequestTimeoutError(error)) {
                throw error
            }
            // Never retry non-retryable errors (e.g. 400, 401, 403). Browser
            // transport TypeErrors are retryable only for callers that do not
            // have a stronger protocol fallback such as direct PUT fanout.
            if (!isRetryableHttpError(error, options?.retryBrowserTransportErrors === true)) {
                throw error
            }
            if (attempt >= maxAttempts) break
            const delayMs = retryDelayMs(error, attempt)
            sdkConsole.warn(`${options?.logPrefix ?? '[secureblob:http]'} request:retry`, {
                label: options?.label ?? null,
                attempt,
                maxAttempts,
                delayMs,
                error: error instanceof Error ? error.message : String(error),
            })
            await delay(delayMs)
        }
    }
    throw lastError instanceof Error ? lastError : new Error('Request failed')
}

function isValidObjectHash(hash: string | null | undefined): hash is string {
    return typeof hash === 'string' && hash.length > 0
}

const SERVER_OBJECT_HASH_PATTERN = /^[a-f0-9]{64}$/

function isValidServerObjectHash(hash: string): boolean {
    return SERVER_OBJECT_HASH_PATTERN.test(hash)
}

function isBrowserLikeObjectStoreRuntime(): boolean {
    if (typeof window !== 'undefined') {
        return true
    }
    const globalScope = globalThis as typeof globalThis & {
        WorkerGlobalScope?: WorkerGlobalScopeConstructor
        self?: object
    }
    const workerGlobalScope = globalScope.WorkerGlobalScope
    return typeof workerGlobalScope === 'function'
        && globalScope.self != null
        && globalScope.self instanceof workerGlobalScope
}

function isRetryableImmutableObjectReadError(error: unknown): boolean {
    if (isAbortError(error)) {
        return !abortFetchOnTimeout()
    }
    if (error instanceof EngineException) {
        return error.error.retryable
    }
    if (error instanceof Error) {
        return (
            error.message.startsWith('Request timed out after ')
            || error.message.startsWith('Response body timed out after ')
            || error.name === 'TypeError'
        )
    }
    return false
}

function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError'
}

function abortFetchOnTimeout(): boolean {
    const value = readNodeEnv(ABORT_FETCH_ON_TIMEOUT_ENV)
    return value == null || value.trim() !== '0'
}

async function cancelResponseBody(res: Response, options: { allowBodyUsed?: boolean } = {}): Promise<void> {
    if (!abortFetchOnTimeout()) {
        return
    }
    if (res.body == null || (res.bodyUsed && !options.allowBodyUsed)) {
        return
    }
    try {
        await Promise.race([
            Promise.resolve(res.body.cancel()),
            new Promise<void>(resolve => {
                setTimeout(resolve, RESPONSE_CANCEL_TIMEOUT_MS)
            }),
        ])
    } catch {
        // Ignore cleanup failures. This helper is best-effort.
    }
}

async function consumeResponseBodyBestEffort(
    res: Response,
    reader: () => Promise<unknown>,
    label: string,
    timeoutMs = responseBodyTimeoutMs(),
): Promise<void> {
    if (res.body == null || res.bodyUsed) {
        return
    }

    try {
        await readResponseBodyWithTimeout(
            res,
            reader,
            timeoutMs,
            label,
        )
    } catch (error) {
        const alreadyCancelledByTimeout =
            error instanceof Error
            && error.message.startsWith('Response body timed out after ')
        if (!alreadyCancelledByTimeout) {
            await cancelResponseBody(res)
        }
    }
}

async function settleSuccessResponseBody(res: Response, label: string): Promise<void> {
    if (res.body == null || res.bodyUsed) {
        return
    }

    const contentLengthHeader = res.headers.get('content-length')
    const hasExplicitEmptyBody =
        res.status === 204
        || res.status === 205
        || res.status === 304
        || contentLengthHeader === '0'

    // Browser worker runtimes have shown long-tail stalls when awaiting
    // arrayBuffer()/text() on explicit empty success bodies. There is nothing
    // to drain in that case, so prefer a bounded cancel and move on.
    if (hasExplicitEmptyBody) {
        await cancelResponseBody(res)
        return
    }

    if (typeof res.arrayBuffer === 'function') {
        await consumeResponseBodyBestEffort(
            res,
            () => res.arrayBuffer(),
            label,
            SUCCESS_RESPONSE_BODY_DRAIN_TIMEOUT_MS,
        )
        return
    }

    if (typeof res.text === 'function') {
        await consumeResponseBodyBestEffort(
            res,
            () => res.text(),
            label,
            SUCCESS_RESPONSE_BODY_DRAIN_TIMEOUT_MS,
        )
        return
    }

    await cancelResponseBody(res)
}

function readResponseBodyWithTimeout<T>(
    res: Response,
    reader: () => Promise<T>,
    timeoutMs: number,
    label: string,
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(() => {
            if (abortFetchOnTimeout()) {
                void cancelResponseBody(res, { allowBodyUsed: true })
            }
            reject(new Error(`Response body timed out after ${timeoutMs}ms: ${label}`))
        }, timeoutMs)

        reader().then(
            value => {
                clearTimeout(timeout)
                resolve(value)
            },
            error => {
                clearTimeout(timeout)
                reject(error)
            },
        )
    })
}

async function readBinaryResponseBodyWithTimeout(
    res: Response,
    timeoutMs: number,
    label: string,
): Promise<Uint8Array> {
    if (typeof res.arrayBuffer === 'function') {
        return new Uint8Array(await readResponseBodyWithTimeout(
            res,
            () => res.arrayBuffer(),
            timeoutMs,
            label,
        ))
    }

    throw new Error(`Response body is not readable: ${label}`)
}

function withOperationTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    label: string,
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(() => {
            reject(new Error(`Request timed out after ${timeoutMs}ms: ${label}`))
        }, timeoutMs)

        promise.then(
            value => {
                clearTimeout(timeout)
                resolve(value)
            },
            error => {
                clearTimeout(timeout)
                reject(error)
            },
        )
    })
}

// ── Memory Backend ─────────────────────────────────────────────

/**
 * Request persistent storage so the browser grants a larger quota (often up to
 * disk size) and refrains from evicting IDB data when disk pressure rises.
 * Best-effort and never rejects - not all browsers grant the request, and
 * headless/test contexts may not implement it. Safe to call repeatedly.
 */
export async function ensurePersistentBrowserStorage(): Promise<boolean> {
    try {
        const nav = globalThis.navigator as (Navigator & { storage?: StorageManager }) | undefined
        if (nav?.storage == null) return false
        if (typeof nav.storage.persisted !== 'function') return false
        const already = await nav.storage.persisted()
        if (already) return true
        if (typeof nav.storage.persist !== 'function') return false
        return await nav.storage.persist()
    } catch (err) {
        sdkConsole.warn('[IDB] ensurePersistentBrowserStorage failed (non-fatal):', err)
        return false
    }
}

/**
 * Read the current browser storage estimate (usage/quota). Returns null in
 * non-browser contexts or when StorageManager is unavailable.
 */
export async function getBrowserStorageEstimate(): Promise<{ usage: number; quota: number } | null> {
    try {
        const nav = globalThis.navigator as (Navigator & { storage?: StorageManager }) | undefined
        if (nav?.storage?.estimate == null) return null
        const { usage = 0, quota = 0 } = await nav.storage.estimate()
        return { usage, quota }
    } catch {
        return null
    }
}

export class MemoryStoreBackend implements StoreBackend, SyncBackend {
    objects = new Map<string, Uint8Array>()
    heads = new Map<string, string[]>()

    async load(hash: string): Promise<Uint8Array | null> { return this.objects.get(hash) ?? null }
    async hasObject(hash: string): Promise<boolean> { return this.objects.has(hash) }
    async findMissingObjects(hashes: string[]): Promise<string[]> {
        return hashes.filter(hash => !this.objects.has(hash))
    }
    async store(hash: string, data: Uint8Array): Promise<void> {
        // console.log(`[MemoryStore] store ${hash.slice(0, 8)} (${data.byteLength} bytes)`)
        this.objects.set(hash, data)
    }

    outbox = new Map<string, Map<string, Uint8Array>>()

    // Outbox (optional)
    async loadOutbox(blobId: string): Promise<Array<{ hash: string; data: Uint8Array }>> {
        const blobOutbox = this.outbox.get(blobId)
        if (!blobOutbox) return []
        return Array.from(blobOutbox.entries()).map(([hash, data]) => ({ hash, data }))
    }

    async storeOutbox(blobId: string, hash: string, data: Uint8Array): Promise<void> {

        let blobOutbox = this.outbox.get(blobId)
        if (!blobOutbox) {
            blobOutbox = new Map()
            this.outbox.set(blobId, blobOutbox)
        }
        blobOutbox.set(hash, data)
    }

    async clearOutbox(blobId: string, hashes: string[]): Promise<void> {

        const blobOutbox = this.outbox.get(blobId)
        if (!blobOutbox) return
        for (const hash of hashes) {
            blobOutbox.delete(hash)
        }
    }

    // Head management (optional)
    async getLocalHeads(blobId: string): Promise<string[]> { return this.heads.get(`local:${blobId}`) ?? [] }
    async setLocalHeads(blobId: string, h: string[]): Promise<void> { this.heads.set(`local:${blobId}`, h) }
    async getRemoteHead(blobId: string): Promise<string | undefined> { return this.heads.get(`remote:${blobId}`)?.[0] }
    async setRemoteHead(blobId: string, h: string): Promise<void> { this.heads.set(`remote:${blobId}`, [h]) }

    async loadBatch(hashes: string[]): Promise<Map<string, Uint8Array>> {
        const result = new Map<string, Uint8Array>()
        for (const hash of hashes) {
            const data = this.objects.get(hash)
            if (data) result.set(hash, data)
        }
        return result
    }

    async storeBatch(entries: Array<{ hash: string; data: Uint8Array }>): Promise<void> {
        for (const { hash, data } of entries) {
            await this.store(hash, data)
        }
    }

    async getHeads(blobId: string, _capability?: string, lane?: string): Promise<HeadsResult> {
        return { heads: this.heads.get(lane == null ? blobId : `${blobId}|lane|${lane}`) ?? [] }
    }

    async updateHead(blobId: string, base: string[], newHead: string, _capability?: string, _referencedObjects?: string[], _prefetchHints?: string[], _leaseId?: string, _releaseLeaseId?: string, lane?: string): Promise<HeadUpdateResult> {
        // Server spec §8.2.1: base must have length 0 or 1
        if (base.length > 1) {
            return { ok: false, code: 'COMMIT_INVALID', message: 'base must be an array with length 0 or 1.' }
        }
        const headKey = lane == null ? blobId : `${blobId}|lane|${lane}`
        const current = this.heads.get(headKey) ?? []
        // CAS check: currentHeads must exactly match base
        const currentStr = JSON.stringify(current.sort())
        const baseStr = JSON.stringify([...base].sort())
        if (currentStr !== baseStr) {
            return { ok: false, code: 'CAS_MISMATCH', remoteHead: current[0] ?? '' }
        }
        this.heads.set(headKey, [newHead])
        return { ok: true }
    }

    /**
     * M1 FIX (mirror of IdbStoreBackend.runGC): Proactive garbage collection.
     * Evicts oldest objects when count exceeds threshold, skipping protected hashes.
     */
    async runGC(maxObjects = 10_000, protectedHashes?: Set<string>): Promise<number> {
        if (this.objects.size <= maxObjects) return 0
        const toEvict = this.objects.size - maxObjects
        let evicted = 0
        for (const hash of this.objects.keys()) {
            if (evicted >= toEvict) break
            if (protectedHashes?.has(hash)) continue
            this.objects.delete(hash)
            evicted++
        }
        return evicted
    }

    async softDeleteBlob(blobId: string): Promise<void> {
        this.heads.delete(blobId)
    }
}

// ── IDB Backend (Local) ────────────────────────────────────────

interface SecureBlobDB extends DBSchema {
    objects: {
        key: string
        value: Uint8Array
    }
    heads: {
        key: string // blobId
        value: { heads: string[]; remoteHead?: string }
    }
    outbox: {
        key: [string, number] // [blobId, timestamp]
        value: { hash: string; bytes: Uint8Array; kind: 'object' | 'commit' }
    }
}

export class IdbStoreBackend implements StoreBackend {
    private dbPromise: Promise<IDBPDatabase<SecureBlobDB>>

    constructor(dbName = 'secureblob-v1') {
        this.dbPromise = openDB<SecureBlobDB>(dbName, 2, {
            upgrade(db) {
                if (!db.objectStoreNames.contains('objects')) db.createObjectStore('objects')
                if (!db.objectStoreNames.contains('heads')) db.createObjectStore('heads')
                if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox')
            },
            // T3: Handle version upgrades from other tabs — auto-close stale connection
            blocking(currentVersion, blockedVersion, event) {
                const db = (event.target as IDBOpenDBRequest)?.result
                if (db) db.close()
                // Reload to pick up new schema — the tab was running stale code anyway
                if (typeof window !== 'undefined') window.location.reload()
            },
        })
    }

    async load(hash: string): Promise<Uint8Array | null> {
        const db = await this.dbPromise
        return (await db.get('objects', hash)) ?? null
    }

    async hasObject(hash: string): Promise<boolean> {
        const db = await this.dbPromise
        return (await db.getKey('objects', hash)) != null
    }

    async findMissingObjects(hashes: string[]): Promise<string[]> {
        const db = await this.dbPromise
        const tx = db.transaction('objects', 'readonly')
        const checks = await Promise.all(hashes.map(async hash => ({
            hash,
            exists: await tx.store.getKey(hash) != null,
        })))
        await tx.done
        return checks.filter(check => !check.exists).map(check => check.hash)
    }

    async store(hash: string, data: Uint8Array): Promise<void> {
        const db = await this.dbPromise
        try {
            await db.put('objects', data, hash)
        } catch (err: unknown) {
            if ((err as DOMException)?.name === 'QuotaExceededError') {
                // Brist B fix: evict by usage pressure (usage/quota), not a fixed
                // object count. runGCByUsage sweeps until usage drops below the safe
                // ratio, falling back to a fixed eviction if estimate() is unavailable.
                sdkConsole.warn('[IDB] QuotaExceededError on store — reclaiming usage pressure')
                await this.evictByUsagePressure(50)
                await db.put('objects', data, hash) // retry once
            } else { throw err }
        }
    }

    /** T5: Evict oldest N objects to free quota. Best-effort. */
    private async evictOldObjects(count: number): Promise<void> {
        const db = await this.dbPromise
        const tx = db.transaction('objects', 'readwrite')
        let cursor = await tx.store.openCursor()
        let deleted = 0
        while (cursor && deleted < count) {
            await cursor.delete()
            deleted++
            cursor = await cursor.continue()
        }
        await tx.done
    }

    /**
     * Brist B fix: Evict objects to reclaim quota. Tries a usage-driven sweep
     * first (evicting until usage/quota drops below the safe ratio), then falls
     * back to a fixed-count eviction if StorageManager.estimate() is unavailable.
     */
    private async evictByUsagePressure(minCount: number, protectedHashes?: Set<string>): Promise<void> {
        const estimate = await getBrowserStorageEstimate()
        if (estimate != null && estimate.quota > 0 && estimate.usage / estimate.quota >= 0.9) {
            await this.runGCByUsage(protectedHashes, 0.85)
            return
        }
        await this.evictOldObjects(minCount)
    }

    /**
     * Brist B fix: usage-driven garbage collection. Evicts oldest unprotected
     * objects until navigator.storage.estimate() reports usage/quota below
     * `maxUsageRatio`. Returns the number of objects evicted. If
     * StorageManager.estimate() is unavailable or quota is zero, this is a no-op
     * (the count-based runGC handles that case).
     */
    async runGCByUsage(protectedHashes?: Set<string>, maxUsageRatio = 0.8): Promise<number> {
        try {
            const estimate = await getBrowserStorageEstimate()
            if (estimate == null || estimate.quota === 0) return 0
            if (estimate.usage / estimate.quota < maxUsageRatio) return 0
            const batchSize = 200
            const maxTotal = 5000
            let evicted = 0
            for (let i = 0; i < 25 && evicted < maxTotal; i++) {
                const cur = await getBrowserStorageEstimate()
                if (cur == null || cur.quota === 0 || cur.usage / cur.quota < maxUsageRatio) break
                const removed = await this.evictOldestBatch(batchSize, protectedHashes)
                if (removed === 0) break
                evicted += removed
            }
            if (evicted > 0) {
                sdkConsole.info(`[IDB] runGCByUsage evicted ${evicted} objects (ratio target ${maxUsageRatio})`)
            }
            return evicted
        } catch (err) {
            sdkConsole.warn('[IDB] runGCByUsage failed (non-fatal):', err)
            return 0
        }
    }

    /** Evict up to `count` oldest objects, skipping protectedHashes. Returns number removed. */
    private async evictOldestBatch(count: number, protectedHashes?: Set<string>): Promise<number> {
        const db = await this.dbPromise
        const tx = db.transaction('objects', 'readwrite')
        let cursor = await tx.store.openCursor()
        let removed = 0
        while (cursor && removed < count) {
            const hash = cursor.key as string
            if (!protectedHashes?.has(hash)) {
                await cursor.delete()
                removed++
            }
            cursor = await cursor.continue()
        }
        await tx.done
        return removed
    }

    /**
     * M1 FIX: Proactive garbage collection — evict oldest objects
     * when count exceeds threshold. Best-effort, fire-and-forget safe.
     * R4: protectedHashes prevents eviction of current commit's object graph.
     * @returns Number of objects evicted (0 if below threshold).
     */
    async runGC(maxObjects = 10_000, protectedHashes?: Set<string>): Promise<number> {
        // Brist B fix: also enforce a usage cap so large object payloads
        // (e.g. sqlite3mt page blobs) don't exhaust quota while count < maxObjects.
        void this.runGCByUsage(protectedHashes, 0.9)
        try {
            const db = await this.dbPromise
            const count = await db.count('objects')
            if (count <= maxObjects) return 0
            const toEvict = count - maxObjects

            // Evict objects by iterating records (hash-sorted, effectively random).
            // IDB key cursors cannot delete records; use a value cursor so
            // browser GC is actually able to reclaim object rows.
            const tx = db.transaction('objects', 'readwrite')
            let cursor = await tx.store.openCursor()
            let evicted = 0
            while (cursor && evicted < toEvict) {
                const hash = cursor.key as string
                if (!protectedHashes?.has(hash)) {
                    await cursor.delete()
                    evicted++
                }
                cursor = await cursor.continue()
            }
            await tx.done
            return evicted
        } catch (err) {
            sdkConsole.warn('[IDB] GC failed (non-fatal):', err)
            return 0
        }
    }

    async loadBatch(hashes: string[]): Promise<Map<string, Uint8Array>> {
        const db = await this.dbPromise
        const result = new Map<string, Uint8Array>()
        const tx = db.transaction('objects', 'readonly')
        await Promise.all(hashes.map(async hash => {
            const data = await tx.store.get(hash)
            if (data) result.set(hash, data)
        }))
        return result
    }

    async storeBatch(entries: Array<{ hash: string; data: Uint8Array }>): Promise<void> {
        const db = await this.dbPromise
        const tx = db.transaction('objects', 'readwrite')
        await Promise.all(entries.map(entry => tx.store.put(entry.data, entry.hash)))
        await tx.done
    }

    // Heads management (Local only)
    async getLocalHeads(blobId: string): Promise<string[]> {
        const db = await this.dbPromise
        const record = await db.get('heads', blobId)
        return record?.heads ?? []
    }

    /**
     * T1 FIX: Atomic head update — sets local heads and/or remote head
     * in a single readwrite transaction to prevent cross-tab lost-update races.
     */
    async setHeadsAtomically(blobId: string, opts: { local?: string[]; remote?: string }): Promise<void> {
        const db = await this.dbPromise
        const tx = db.transaction('heads', 'readwrite')
        const record = (await tx.store.get(blobId)) ?? { heads: [] }
        if (opts.local !== undefined) record.heads = opts.local
        if (opts.remote !== undefined) record.remoteHead = opts.remote
        await tx.store.put(record, blobId)
        await tx.done
    }

    /** @deprecated Use setHeadsAtomically instead for multi-field updates */
    async setLocalHeads(blobId: string, heads: string[]): Promise<void> {
        await this.setHeadsAtomically(blobId, { local: heads })
    }

    // Remote tracking (Local perspective)
    async getRemoteHead(blobId: string): Promise<string | undefined> {
        const db = await this.dbPromise
        const record = await db.get('heads', blobId)
        return record?.remoteHead
    }

    /** @deprecated Use setHeadsAtomically instead for multi-field updates */
    async setRemoteHead(blobId: string, head: string): Promise<void> {
        await this.setHeadsAtomically(blobId, { remote: head })
    }

    // --- Outbox Persistence ---

    async loadOutbox(blobId: string): Promise<Array<{ hash: string; data: Uint8Array }>> {
        const db = await this.dbPromise
        const tx = db.transaction('outbox', 'readonly')
        const range = IDBKeyRange.bound([blobId, 0], [blobId, Infinity])
        let cursor = await tx.store.openCursor(range)
        const entries: Array<{ hash: string; data: Uint8Array }> = []
        while (cursor) {
            entries.push({ hash: cursor.value.hash, data: cursor.value.bytes })
            cursor = await cursor.continue()
        }
        return entries
    }

    async storeOutbox(blobId: string, hash: string, data: Uint8Array): Promise<void> {
        const db = await this.dbPromise
        const tx = db.transaction('outbox', 'readwrite')
        await tx.store.put({ hash, bytes: data, kind: 'object' }, [blobId, Date.now() + Math.random()])
        await tx.done
    }

    async clearOutbox(blobId: string, hashes: string[]): Promise<void> {
        const db = await this.dbPromise
        const tx = db.transaction('outbox', 'readwrite')
        const hashSet = new Set(hashes)
        const range = IDBKeyRange.bound([blobId, 0], [blobId, Infinity])
        let cursor = await tx.store.openCursor(range)
        while (cursor) {
            if (hashSet.has(cursor.value.hash)) {
                await cursor.delete()
            }
            cursor = await cursor.continue()
        }
        await tx.done
    }
}

// ── HTTP Backend (Remote) ──────────────────────────────────────

export class HttpStoreBackend implements StoreBackend, SyncBackend {
    private batchPutDisabledForSession = false
    private readonly activeRequests = new Map<number, {
        method: string
        path: string
        timeoutMs: number | null
        startedAt: number
        phase: string
        status: number | null
    }>()

    constructor(
        private readonly apiBaseUrl: string,
        private readonly capability: string,
        private readonly blobId?: string,
        private readonly authProvider?: WorkspaceAuthProvider,
        private readonly debugTag?: string,
    ) { }

    private logPrefix(headers?: Record<string, string>): '[secureblob:http]' | '[sqlite3mt:http]' {
        return this.debugTag != null || headers?.[ATTRIBUTION_WORKLOAD_HEADER] === 'sqlite3mt'
            ? '[sqlite3mt:http]'
            : '[secureblob:http]'
    }

    getDebugState(): {
        debugTag: string | null
        activeRequests: Array<{
            requestId: number
            method: string
            path: string
            timeoutMs: number | null
            ageMs: number
            phase: string
            status: number | null
        }>
    } {
        const now = Date.now()
        return {
            debugTag: this.debugTag ?? null,
            activeRequests: Array.from(this.activeRequests.entries()).map(([requestId, request]) => ({
                requestId,
                method: request.method,
                path: request.path,
                timeoutMs: request.timeoutMs,
                ageMs: now - request.startedAt,
                phase: request.phase,
                status: request.status,
            })),
        }
    }

    private trackRequestStart(requestId: number, method: string, path: string, timeoutMs?: number): void {
        this.activeRequests.set(requestId, {
            method,
            path,
            timeoutMs: timeoutMs ?? null,
            startedAt: Date.now(),
            phase: 'fetch',
            status: null,
        })
    }

    private trackRequestPhase(requestId: number, phase: string, status?: number | null): void {
        const existing = this.activeRequests.get(requestId)
        if (existing == null) {
            return
        }
        existing.phase = phase
        if (status !== undefined) {
            existing.status = status
        }
    }

    private attachTrackedRequestId(res: Response, requestId: number): Response {
        ;(res as Response & { [HTTP_REQUEST_ID]?: number })[HTTP_REQUEST_ID] = requestId
        return res
    }

    private trackedRequestId(res: Response): number | null {
        return (res as Response & { [HTTP_REQUEST_ID]?: number })[HTTP_REQUEST_ID] ?? null
    }

    private markResponsePhase(res: Response, phase: string): void {
        const requestId = this.trackedRequestId(res)
        if (requestId != null) {
            this.trackRequestPhase(requestId, phase)
        }
    }

    private clearTrackedResponse(res: Response): void {
        const requestId = this.trackedRequestId(res)
        if (requestId != null) {
            this.activeRequests.delete(requestId)
        }
    }

    private headers(
        extra?: Record<string, string>,
        capability?: string,
        workload?: 'workspace-fs' | 'sqlite3mt',
    ): Record<string, string> {
        const resolvedWorkload = workload ?? (this.debugTag ? 'sqlite3mt' : 'workspace-fs')
        return {
            'content-type': 'application/octet-stream',
            authorization: `Bearer ${capability ?? this.capability}`,
            [ATTRIBUTION_CLIENT_HEADER]: 'js-sdk',
            [ATTRIBUTION_WORKLOAD_HEADER]: resolvedWorkload,
            [ATTRIBUTION_WORKSPACE_TYPE_HEADER]: 'normal',
            ...(this.debugTag ? { 'x-sqlite3mt-debug-tag': this.debugTag } : {}),
            ...getEphemeralHeaders(),
            ...extra
        }
    }

    private async request(
        method: string,
        path: string,
        headers: Record<string, string>,
        body?: unknown,
        timeoutMs?: number,
        maxAttempts = 1,
        cacheMode?: RequestCache,
        scopeOverride?: 'read' | 'write',
        retryRequestTimeouts = true,
        retryBrowserTransportErrors = false,
    ): Promise<Response> {
        const requireWrite = method === 'PUT' || method === 'POST' || method === 'DELETE'
        const scope = scopeOverride ?? (requireWrite ? 'write' as const : 'read' as const)
        const requestLabel = `${method} ${path}`

        const resolveHeaders = async (): Promise<Record<string, string>> => {
            if (!this.authProvider) {
                return headers
            }
            try {
                const tokenPromise = this.authProvider.getAccessToken(scope)
                const token = timeoutMs != null
                    ? await withOperationTimeout(tokenPromise, timeoutMs, requestLabel)
                    : await tokenPromise
                return { ...headers, authorization: `Bearer ${token.token}` }
            } catch (error) {
                if (
                    timeoutMs != null
                    && error instanceof Error
                    && error.message === `Request timed out after ${timeoutMs}ms: ${requestLabel}`
                ) {
                    // A hung token/session acquire must not pin the scope behind a
                    // stale in-flight promise; let the next retry start fresh.
                    this.authProvider.invalidateToken(scope)
                }
                throw error
            }
        }

        const resolvedHeaders = await resolveHeaders()
        const res = await this.executeRequest(method, path, resolvedHeaders, body, timeoutMs, maxAttempts, cacheMode, retryRequestTimeouts, retryBrowserTransportErrors)

        // 401 retry with fresh token when using auth provider (Spec §10.2).
        if (res.status === 401 && this.authProvider) {
            try {
                await cancelResponseBody(res)
            } finally {
                this.clearTrackedResponse(res)
            }
            this.authProvider.invalidateToken(scope)
            const retryHeaders = await resolveHeaders()
            const retryRes = await this.executeRequest(method, path, retryHeaders, body, timeoutMs, maxAttempts, cacheMode, retryRequestTimeouts, retryBrowserTransportErrors)
            if (retryRes.status !== 401) {
                return retryRes
            }

            try {
                recordBrowserHttpErrorResponse(retryRes, { url: `${this.apiBaseUrl}${path}`, method })
                await cancelResponseBody(retryRes)
            } finally {
                this.clearTrackedResponse(retryRes)
            }

            const error: EngineError = {
                code: 'NETWORK_ERROR',
                message: `Request failed 401 after token refresh: ${requestLabel}`,
                retryable: false,
                details: {
                    request: {
                        method,
                        url: `${this.apiBaseUrl}${path}`,
                        headers: { ...retryHeaders, authorization: 'REDACTED' },
                        body,
                    },
                    response: {
                        status: retryRes.status,
                        headers: Object.fromEntries(retryRes.headers.entries()),
                    },
                },
            }
            throw new EngineException(error)
        }

        return res
    }

    private async executeRequest(
        method: string,
        path: string,
        headers: Record<string, string>,
        body?: unknown,
        timeoutMs?: number,
        maxAttempts = 1,
        cacheMode?: RequestCache,
        retryRequestTimeouts = true,
        retryBrowserTransportErrors = false,
    ): Promise<Response> {
        return withRetry(async () => {
            const url = `${this.apiBaseUrl}${path}`
            const requestId = ++httpRequestSeq
            this.trackRequestStart(requestId, method, path, timeoutMs)

            // Binary bodies (Uint8Array, ArrayBuffer) must be passed through directly.
            // Only non-binary, non-string bodies get JSON.stringify'd.
            let fetchBody: BodyInit | undefined
            if (body == null) {
                fetchBody = undefined
            } else if (body instanceof Uint8Array || body instanceof ArrayBuffer) {
                fetchBody = body as BodyInit // fetch natively handles binary types
            } else if (typeof body === 'string') {
                fetchBody = body
            } else {
                fetchBody = JSON.stringify(body)
            }

            // Componentized preview2 fetch currently has a bad abort failure path;
            // server jobs keep the logical timeout/retry without aborting the fetch.
            const abortTimedOutFetch = timeoutMs != null && abortFetchOnTimeout()
            const controller = abortTimedOutFetch ? new AbortController() : undefined
            let timeoutHandle: ReturnType<typeof setTimeout> | undefined
            let timeoutFired = false

            let res: Response
            try {
                const logPrefix = this.logPrefix(headers)
                sdkConsole.trace(`${logPrefix} request:start`, {
                    requestId,
                    method,
                    path,
                    timeoutMs: timeoutMs ?? null,
                })
                const fetchPromise = fetch(url, {
                    method,
                    headers,
                    body: fetchBody,
                    // Default GET behavior remains fresh polling (`no-cache`) for
                    // mutable metadata like /heads. Immutable CAS requests can opt
                    // into a different cache mode explicitly, including POST
                    // batch probes that still need to bypass browser caches.
                    ...(cacheMode != null
                        ? { cache: cacheMode }
                        : (method === 'GET' ? { cache: 'no-cache' as RequestCache } : {})),
                    ...(controller ? { signal: controller.signal } : {}),
                })

                if (timeoutMs != null && controller != null) {
                    const timeoutPromise = new Promise<Response>((_, reject) => {
                        timeoutHandle = setTimeout(() => {
                            timeoutFired = true
                            controller.abort()
                            reject(new Error(`Request timed out after ${timeoutMs}ms: ${method} ${path}`))
                        }, timeoutMs)
                    })
                    res = await Promise.race([fetchPromise, timeoutPromise])
                } else if (timeoutMs != null) {
                    const timeoutPromise = new Promise<Response>((_, reject) => {
                        timeoutHandle = setTimeout(() => {
                            timeoutFired = true
                            reject(new Error(`Request timed out after ${timeoutMs}ms: ${method} ${path}`))
                        }, timeoutMs)
                    })
                    res = await Promise.race([fetchPromise, timeoutPromise])
                } else {
                    res = await fetchPromise
                }
                sdkConsole.trace(`${logPrefix} request:response`, {
                    requestId,
                    method,
                    path,
                    status: res.status,
                })
                this.trackRequestPhase(requestId, 'headers', res.status)
                if (!(res.status === 401 && this.authProvider)) {
                    recordBrowserHttpErrorResponse(res, { url, method })
                }
            } catch (error) {
                if (
                    timeoutMs != null
                    && (
                        (error instanceof Error && error.message === `Request timed out after ${timeoutMs}ms: ${method} ${path}`)
                        || (timeoutFired && (isAbortError(error) || error instanceof TypeError))
                    )
                ) {
                    sdkConsole.warn(`${this.logPrefix(headers)} request:timeout`, {
                        requestId,
                        method,
                        path,
                        timeoutMs,
                    })
                    this.activeRequests.delete(requestId)
                    throw new Error(`Request timed out after ${timeoutMs}ms: ${method} ${path}`)
                }
                sdkConsole.warn(`${this.logPrefix(headers)} request:error`, {
                    requestId,
                    method,
                    path,
                    error,
                })
                this.activeRequests.delete(requestId)
                throw error
            } finally {
                if (timeoutHandle != null) clearTimeout(timeoutHandle)
            }

            if (!res.ok) {
                const pathWithoutQuery = path.split('?', 1)[0] ?? path
                const allowPassthrough =
                    res.status === 304
                    || res.status === 404
                    || (res.status === 401 && this.authProvider)
                    || (
                        method === 'POST'
                        && pathWithoutQuery.endsWith('/heads')
                        && (res.status === 409 || res.status === 403)
                    )
                if (!allowPassthrough) {
                    let responseBody: unknown
                    try {
                        const text = await readResponseBodyWithTimeout(
                            res,
                            () => res.text(),
                            responseBodyTimeoutMs(),
                            `${method} ${path} error body`,
                        )
                        try { responseBody = JSON.parse(text) } catch { responseBody = text }
                    } catch { /* ignore */ }
                    const serverCode = extractServerErrorCode(responseBody)

                    const error: EngineError = {
                        code: serverCode,
                        message: `Request failed ${res.status}: ${method} ${path}`,
                        retryable: res.status >= 500 || res.status === 429,
                        details: {
                            request: {
                                method,
                                url,
                                headers: { ...headers, authorization: 'REDACTED' },
                                body: body,
                            },
                            response: {
                                status: res.status,
                                headers: Object.fromEntries(res.headers.entries()),
                                body: responseBody
                            }
                        }
                    }
                    this.activeRequests.delete(requestId)
                    throw new EngineException(error)
                }
            }
            return this.attachTrackedRequestId(res, requestId)
        }, maxAttempts, {
            logPrefix: this.logPrefix(headers),
            label: `${method} ${path}`,
            retryRequestTimeouts,
            retryBrowserTransportErrors,
        })
    }

    async load(hash: string, capability?: string): Promise<Uint8Array | null> {
        if (!isValidObjectHash(hash)) {
            return null
        }
        return await this.readImmutableObject(hash, capability, 'load')
    }

    async hasObject(hash: string, capability?: string): Promise<boolean> {
        if (!isValidObjectHash(hash)) {
            return false
        }
        return (await this.readImmutableObject(hash, capability, 'probe')) != null
    }

    async findMissingObjects(hashes: string[], capability?: string): Promise<string[]> {
        const requestedHashes = Array.from(new Set(hashes))
        const invalidHashes = requestedHashes.filter(hash => !isValidObjectHash(hash) || !isValidServerObjectHash(hash))
        if (invalidHashes.length > 0) {
            throw new Error(`Invalid object hash for POST /objects/missing: ${invalidHashes.join(', ')}`)
        }
        const normalizedHashes = requestedHashes.filter(isValidServerObjectHash)
        if (normalizedHashes.length === 0) {
            return []
        }
        const requestedSet = new Set(normalizedHashes)

        const logPrefix = this.logPrefix()
        sdkConsole.trace(`${logPrefix} findMissingObjects:start`, {
            count: normalizedHashes.length,
            hashes: normalizedHashes,
        })
        const res = await this.request(
            'POST',
            `/blobs/${encodeURIComponent(this.blobId!)}/objects/missing`,
            this.headers({ 'content-type': 'application/json' }, capability),
            { hashes: normalizedHashes },
            objectRequestTimeoutMs(),
            RETRYABLE_HTTP_MAX_ATTEMPTS,
            'no-store',
            'read',
            true,
            true,
        )

        if (!res.ok) {
            try {
                await cancelResponseBody(res)
            } finally {
                this.clearTrackedResponse(res)
            }
            throw new Error(`Failed findMissingObjects ${res.status}`)
        }

        try {
            const payload = await res.json() as { missing?: unknown }
            if (!Array.isArray(payload.missing) || payload.missing.some(hash => typeof hash !== 'string')) {
                throw new Error('Invalid JSON payload from POST /objects/missing')
            }
            const missing = payload.missing as string[]
            const invalidMissing = missing.filter(hash => !isValidServerObjectHash(hash) || !requestedSet.has(hash))
            if (invalidMissing.length > 0) {
                throw new Error(`Invalid missing object hash from POST /objects/missing: ${invalidMissing.join(', ')}`)
            }
            sdkConsole.trace(`${logPrefix} findMissingObjects:done`, {
                requested: normalizedHashes.length,
                missing: missing.length,
            })
            return missing
        } finally {
            this.clearTrackedResponse(res)
        }
    }

    private async readImmutableObject(
        hash: string,
        capability: string | undefined,
        purpose: 'load' | 'probe',
    ): Promise<Uint8Array | null> {
        const path = `/blobs/${encodeURIComponent(this.blobId!)}/objects/${encodeURIComponent(hash)}`
        const label = purpose === 'probe'
            ? `GET object probe ${hash}`
            : `GET ${path}`

        for (let attempt = 1; attempt <= IMMUTABLE_OBJECT_MAX_ATTEMPTS; attempt++) {
            let res: Response | null = null
            try {
                res = await this.request(
                    'GET',
                    path,
                    this.headers(undefined, capability),
                    undefined,
                    immutableObjectRequestTimeoutMs(),
                    1,
                    // Immutable-object reads must bypass browser HTTP cache
                    // entirely. sqlite3mt already maintains its own local CAS
                    // and hidden fetch caching has caused cross-worker stalls.
                    'no-store',
                    'read',
                )
                if (res.status === 404) {
                    try {
                        await cancelResponseBody(res)
                    } finally {
                        this.clearTrackedResponse(res)
                    }
                    return null
                }
                if (!res.ok) {
                    try {
                        await cancelResponseBody(res)
                    } finally {
                        this.clearTrackedResponse(res)
                    }
                    throw new Error(`Failed ${purpose} ${res.status}`)
                }

                this.markResponsePhase(res, 'body-binary')
                return await readBinaryResponseBodyWithTimeout(
                    res,
                    immutableObjectResponseBodyTimeoutMs(),
                    label,
                )
            } catch (error) {
                if (attempt >= IMMUTABLE_OBJECT_MAX_ATTEMPTS || !isRetryableImmutableObjectReadError(error)) {
                    throw error
                }
                sdkConsole.warn(`${this.logPrefix()} immutable-object:retry`, {
                    purpose,
                    hash,
                    attempt,
                    maxAttempts: IMMUTABLE_OBJECT_MAX_ATTEMPTS,
                    error,
                })
                await delay(100 * attempt)
            } finally {
                if (res != null) {
                    this.clearTrackedResponse(res)
                }
            }
        }

        return null
    }

    /** The bulk load operation using explicit batch API */
    async loadBatch(hashes: string[], capability?: string): Promise<Map<string, Uint8Array>> {
        const requestedHashes = Array.from(new Set(hashes.filter(isValidObjectHash)))
        const invalidHashes = requestedHashes.filter(hash => !isValidServerObjectHash(hash))
        if (invalidHashes.length > 0) {
            sdkConsole.warn(`${this.logPrefix()} loadBatch:invalid-hashes-dropped`, {
                count: invalidHashes.length,
                hashes: invalidHashes,
            })
        }
        const normalizedHashes = requestedHashes.filter(isValidServerObjectHash)
        if (normalizedHashes.length === 0) {
            return new Map<string, Uint8Array>()
        }
        const logPrefix = this.logPrefix()
        sdkConsole.trace(`${logPrefix} loadBatch:start`, {
            count: normalizedHashes.length,
            hashes: normalizedHashes,
        })
        const res = await this.request('POST', `/blobs/${encodeURIComponent(this.blobId!)}/objects/batch`,
            this.headers({ 'content-type': 'application/json' }, capability),
            { hashes: normalizedHashes },
            objectRequestTimeoutMs(),
            RETRYABLE_HTTP_MAX_ATTEMPTS,
            // sqlite3mt already maintains an explicit local CAS cache.
            // Browser HTTP cache introduces hidden cross-worker state that
            // can strand immutable object reads during concurrent publish /
            // reconcile loops, so bypass it for batch reads as well.
            'no-store',
            'read',
            true,
            true,
        )

        if (!res.ok) throw new Error(`Failed loadBatch ${res.status}`)
        sdkConsole.trace(`${logPrefix} loadBatch:response-ok`, {
            count: normalizedHashes.length,
            hashes: normalizedHashes,
        })

        this.markResponsePhase(res, 'body-binary')
        const responseBytes = await (async () => {
            try {
                return await readBinaryResponseBodyWithTimeout(
                    res,
                    responseBodyTimeoutMs(),
                    'POST /objects/batch',
                )
            } catch (error) {
                if (isRetryableImmutableObjectReadError(error)) {
                    sdkConsole.warn(`${logPrefix} loadBatch:direct-fallback`, {
                        count: normalizedHashes.length,
                        hashes: normalizedHashes,
                        error,
                    })
                    return null
                }
                throw error
            } finally {
                this.clearTrackedResponse(res)
            }
        })()
        if (responseBytes == null) {
            return await this.loadBatchViaDirectGet(normalizedHashes, capability)
        }
        const responseText = new TextDecoder().decode(responseBytes)
        let payload: { objects: Record<string, string>; missing: string[] }
        try {
            payload = JSON.parse(responseText) as { objects: Record<string, string>; missing: string[] }
        } catch (error) {
            sdkConsole.error(`${logPrefix} loadBatch:invalid-json`, {
                count: normalizedHashes.length,
                hashes: normalizedHashes,
                error,
                responseText,
            })
            throw new Error('Invalid JSON payload from POST /objects/batch')
        }
        const result = new Map<string, Uint8Array>()

        for (const [hash, base64] of Object.entries(payload.objects)) {
            const binaryString = atob(base64)
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i)
            }
            result.set(hash, bytes)
        }
        sdkConsole.trace(`${logPrefix} loadBatch:done`, {
            requested: normalizedHashes,
            found: Array.from(result.keys()),
        })
        return result
    }

    private async loadBatchViaDirectGet(hashes: string[], capability?: string): Promise<Map<string, Uint8Array>> {
        const result = new Map<string, Uint8Array>()
        for (const hash of hashes) {
            const bytes = await this.readImmutableObject(hash, capability, 'load')
            if (bytes != null) {
                result.set(hash, bytes)
            }
        }
        sdkConsole.trace(`${this.logPrefix()} loadBatch:done`, {
            requested: hashes,
            found: Array.from(result.keys()),
            strategy: 'direct-get-fallback',
        })
        return result
    }

    async store(hash: string, data: Uint8Array, capability?: string): Promise<void> {
        const contentHash = await computeSha256Hex(data)
        // cast data to any because fetch accepts Uint8Array but ts definitions vary
        const res = await this.request(
            'PUT',
            `/blobs/${encodeURIComponent(this.blobId!)}/objects/${encodeURIComponent(hash)}`,
            this.headers({ [CONTENT_HASH_HEADER_NAME]: contentHash }, capability),
            data,
            objectRequestTimeoutMs(),
            RETRYABLE_HTTP_MAX_ATTEMPTS,
        )

        // Prefer draining tiny success bodies when they are immediately
        // available so the browser can fully recycle the connection. If the
        // server keeps the stream open, fall back to a bounded cancel.
        try {
            await settleSuccessResponseBody(res, `PUT object ${hash} success`)
        } finally {
            this.clearTrackedResponse(res)
        }
    }

    private async storeBatchViaDirectPut(
        chunk: Array<{ hash: string; data: Uint8Array }>,
        strategy: 'direct-put' | 'direct-put-fallback',
    ): Promise<void> {
        for (const entry of chunk) {
            await this.store(entry.hash, entry.data)
        }
        sdkConsole.trace(`${this.logPrefix()} storeBatch:done`, {
            count: chunk.length,
            hashes: chunk.map(entry => entry.hash),
            strategy,
        })
    }

    private shouldFallbackBatchPutToDirectPut(error: unknown): boolean {
        if (error instanceof EngineException) {
            return false
        }
        return error instanceof TypeError
            || isAbortError(error)
            || isRequestTimeoutError(error)
    }

    /** 
     * §7.4 Batch PUT — upload multiple objects in one HTTP request.
     * Chunks into batches of 50 to respect server limit.
     */
    async storeBatch(entries: Array<{ hash: string; data: Uint8Array }>): Promise<void> {
        const BATCH_SIZE = 50
        for (let i = 0; i < entries.length; i += BATCH_SIZE) {
            const chunk = entries.slice(i, i + BATCH_SIZE)
            const logPrefix = this.logPrefix()
            sdkConsole.trace(`${logPrefix} storeBatch:start`, {
                count: chunk.length,
                hashes: chunk.map(entry => entry.hash),
            })
            // Singleton uploads avoid JSON/base64 overhead. If a browser batch-put
            // stalls once, disable it for this backend instance and keep the
            // conservative sequential direct-PUT path for the rest of the sync.
            if (chunk.length === 1 || this.batchPutDisabledForSession) {
                await this.storeBatchViaDirectPut(chunk, 'direct-put')
                continue
            }
            const batchEntries = await Promise.all(chunk.map(async ({ hash, data }) => {
                // Convert Uint8Array → base64
                let binary = ''
                for (let j = 0; j < data.length; j++) binary += String.fromCharCode(data[j])
                return {
                    hash,
                    contentHash: await computeSha256Hex(data),
                    data: btoa(binary),
                }
            }))
            try {
                const res = await this.request('POST',
                    `/blobs/${encodeURIComponent(this.blobId!)}/objects/batch-put`,
                    this.headers({ 'content-type': 'application/json' }),
                    { entries: batchEntries },
                    batchPutRequestTimeoutMs(),
                    RETRYABLE_HTTP_MAX_ATTEMPTS,
                    undefined,
                    undefined,
                    false,
                )
                // Prefer draining tiny success bodies when they are immediately
                // available so browser connection reuse stays healthy under
                // concurrent writers. If the server keeps the stream open, fall
                // back to a bounded cancel instead of waiting for EOF forever.
                try {
                    await settleSuccessResponseBody(
                        res,
                        `POST /blobs/${encodeURIComponent(this.blobId!)}/objects/batch-put success`,
                    )
                } finally {
                    this.clearTrackedResponse(res)
                }
                sdkConsole.trace(`${logPrefix} storeBatch:done`, {
                    count: chunk.length,
                    hashes: chunk.map(entry => entry.hash),
                    strategy: 'batch-put',
                })
            } catch (error) {
                if (!isBrowserLikeObjectStoreRuntime() || !this.shouldFallbackBatchPutToDirectPut(error)) {
                    throw error
                }
                this.batchPutDisabledForSession = true
                sdkConsole.warn(`${logPrefix} storeBatch:batch-put-fallback`, {
                    count: chunk.length,
                    hashes: chunk.map(entry => entry.hash),
                    error,
                })
                await this.storeBatchViaDirectPut(chunk, 'direct-put-fallback')
            }
        }
    }

    async getHeads(blobId: string, capability?: string, lane?: string): Promise<HeadsResult> {
        const laneQuery = lane != null ? `?lane=${encodeURIComponent(lane)}` : ''

        let res: Response
        try {
            res = await this.request(
                'GET',
                `/blobs/${encodeURIComponent(blobId)}/heads${laneQuery}`,
                this.headers(undefined, capability, lane != null ? 'sqlite3mt' : undefined),
                undefined,
                headRequestTimeoutMs(),
                RETRYABLE_HTTP_MAX_ATTEMPTS,
                // Remote heads are mutable coordination metadata. Always fetch
                // them uncached so concurrent writers observe the latest epoch.
                'no-store',
                undefined,
                true,
                true,
            )
        } catch (error) {
            sdkConsole.error(`${this.logPrefix(lane != null ? { [ATTRIBUTION_WORKLOAD_HEADER]: 'sqlite3mt' } : undefined)} getHeads failed`, { blobId, lane: lane ?? null }, error)
            throw error
        }
        if (res.status === 304) {
            sdkConsole.warn(`${this.logPrefix(lane != null ? { [ATTRIBUTION_WORKLOAD_HEADER]: 'sqlite3mt' } : undefined)} getHeads unexpected-304`, { blobId, lane: lane ?? null })
            this.clearTrackedResponse(res)
            throw new Error(`Unexpected 304 from getHeads for ${blobId}`)
        }
        if (res.status === 404) {
            sdkConsole.trace(`${this.logPrefix(lane != null ? { [ATTRIBUTION_WORKLOAD_HEADER]: 'sqlite3mt' } : undefined)} getHeads 404`, { blobId })
            try {
                await cancelResponseBody(res)
            } finally {
                this.clearTrackedResponse(res)
            }
            return { heads: [] }
        }
        if (!res.ok) {
            sdkConsole.warn(`${this.logPrefix(lane != null ? { [ATTRIBUTION_WORKLOAD_HEADER]: 'sqlite3mt' } : undefined)} getHeads non-ok`, { blobId, status: res.status })
            this.clearTrackedResponse(res)
            throw new Error(`Failed getHeads ${res.status} for ${blobId}`)
        }

        this.markResponsePhase(res, 'body-text')
        const responseText = await (async () => {
            try {
                return await readResponseBodyWithTimeout(
                    res,
                    () => res.text(),
                    responseBodyTimeoutMs(),
                    `GET /blobs/${encodeURIComponent(blobId)}/heads${laneQuery}`,
                )
            } finally {
                this.clearTrackedResponse(res)
            }
        })()
        let payload: { heads?: string[]; prefetchHints?: string[] } | string[]
        try {
            payload = JSON.parse(responseText) as { heads?: string[]; prefetchHints?: string[] } | string[]
        } catch (error) {
            sdkConsole.error(`${this.logPrefix(lane != null ? { [ATTRIBUTION_WORKLOAD_HEADER]: 'sqlite3mt' } : undefined)} getHeads invalid-json`, {
                blobId,
                status: res.status,
                contentType: res.headers.get('content-type'),
                bodyPreview: responseText.slice(0, 200),
            }, error)
            throw error
        }

        const heads = Array.isArray(payload) ? payload : (payload.heads ?? [])
        const prefetchHints = !Array.isArray(payload) ? payload.prefetchHints : undefined

        return { heads, prefetchHints }
    }

    async updateHead(blobId: string, base: string[], newHead: string, capability?: string, referencedObjects?: string[], prefetchHints?: string[], leaseId?: string, releaseLeaseId?: string, lane?: string): Promise<HeadUpdateResult> {
        // Wrap in try/catch: request() throws EngineException for 400 responses.
        // OBJECT_MISSING and COMMIT_INVALID (both 400) are recoverable — return
        // structured results so the sync loop can handle them:
        //  - OBJECT_MISSING → repush objects and retry (§7.4)
        //  - COMMIT_INVALID → merge with remote and retry (§7.2.4)
        let res: Response
        try {
            const laneQuery = lane != null ? `?lane=${encodeURIComponent(lane)}` : ''
            const body: Record<string, unknown> = {
                base, new: newHead,
                referencedObjects: referencedObjects ?? [],
                ...(prefetchHints ? { prefetchHints } : {}),
                ...(leaseId ? { leaseId } : {}),
                ...(releaseLeaseId ? { releaseLeaseId } : {}),
            }
            res = await this.request('POST', `/blobs/${encodeURIComponent(blobId)}/heads${laneQuery}`,
                this.headers({ 'content-type': 'application/json' }, capability, lane != null ? 'sqlite3mt' : undefined),
                body,
                headRequestTimeoutMs(),
                RETRYABLE_HTTP_MAX_ATTEMPTS,
            )
        } catch (error) {
            if (error instanceof EngineException) {
                if (error.error.code === 'OBJECT_MISSING') {
                    return { ok: false, code: 'OBJECT_MISSING', message: error.error.message }
                }
                if (error.error.code === 'COMMIT_INVALID') {
                    return { ok: false, code: 'COMMIT_INVALID', message: error.error.message }
                }
            }
            throw error
        }

        if (res.ok) {
            try {
                await settleSuccessResponseBody(
                    res,
                    `POST /blobs/${encodeURIComponent(blobId)}/heads success`,
                )
            } finally {
                this.clearTrackedResponse(res)
            }
            sdkConsole.debug(`${this.logPrefix(lane != null ? { [ATTRIBUTION_WORKLOAD_HEADER]: 'sqlite3mt' } : undefined)} updateHead OK blob=${blobId.slice(0, 12)} base=[${base.map(h => h.slice(0, 8))}] new=${newHead.slice(0, 8)}`)
            return { ok: true }
        }

        if (res.status === 409) {
            this.markResponsePhase(res, 'body-json')
            const payload = await (async () => {
                try {
                    return await readResponseBodyWithTimeout(
                        res,
                        () => res.json() as Promise<{ heads?: string[]; remoteHead?: string }>,
                        responseBodyTimeoutMs(),
                        `POST /blobs/${encodeURIComponent(blobId)}/heads 409`,
                    )
                } finally {
                    this.clearTrackedResponse(res)
                }
            })()
            const remoteHead = payload.remoteHead ?? payload.heads?.[0] ?? ''
            sdkConsole.warn(`${this.logPrefix(lane != null ? { [ATTRIBUTION_WORKLOAD_HEADER]: 'sqlite3mt' } : undefined)} updateHead 409 CAS_MISMATCH blob=${blobId.slice(0, 12)} base=[${base.map(h => h.slice(0, 8))}] new=${newHead.slice(0, 8)} serverHead=${remoteHead.slice(0, 8)}`)
            return { ok: false, code: 'CAS_MISMATCH', remoteHead }
        }
        if (res.status === 403) {
            try {
                await cancelResponseBody(res)
            } finally {
                this.clearTrackedResponse(res)
            }
            return { ok: false, code: 'FORBIDDEN', message: 'forbidden' }
        }
        if (res.status === 404) {
            try {
                await cancelResponseBody(res)
            } finally {
                this.clearTrackedResponse(res)
            }
            return { ok: false, code: 'NOT_FOUND', message: 'not_found' }
        }

        // Detailed error should have been thrown by request() for other codes, 
        // but if we fell through:
        let responseBody: unknown
        try {
            this.markResponsePhase(res, 'body-json')
            responseBody = await readResponseBodyWithTimeout(
                res,
                () => res.json(),
                responseBodyTimeoutMs(),
                `POST /blobs/${encodeURIComponent(blobId)}/heads error body`,
            )
        } catch { /* ignore */ }
        this.clearTrackedResponse(res)

        const errorCode = extractServerErrorCode(responseBody)

        const error: EngineError = {
            code: errorCode,
            message: `Update head failed: ${res.status}`,
            retryable: false,
            details: {
                response: {
                    status: res.status,
                    headers: Object.fromEntries(res.headers.entries()),
                    body: responseBody
                }
            }
        }
        throw new EngineException(error)
    }

    async softDeleteBlob(blobId: string, capability?: string): Promise<void> {
        await this.request('DELETE', `/blobs/${encodeURIComponent(blobId)}`, this.headers(undefined, capability), undefined, objectRequestTimeoutMs())
    }

    // ── Lease API (§94 §7) ─────────────────────────────────────

    async acquireLease(blobId: string, resourceId: string, mode: number, writerKid: string, ttlSeconds = 30): Promise<{ leaseId: string; mode: number; expiresAt: string; resourceId: string } | null> {
        try {
            const res = await this.request('POST',
                `/blobs/${encodeURIComponent(blobId)}/lease`,
                this.headers({ 'content-type': 'application/json' }),
                { resourceId, mode, writerKid, ttlSeconds }
            )
            if (!res.ok) {
                try { await cancelResponseBody(res) } finally { this.clearTrackedResponse(res) }
                return null
            }
            const payload = await readResponseBodyWithTimeout(
                res,
                () => res.json() as Promise<{ leaseId: string; mode: number; expiresAt: string; resourceId: string }>,
                responseBodyTimeoutMs(),
                `POST /blobs/${encodeURIComponent(blobId)}/lease`,
            )
            this.clearTrackedResponse(res)
            return payload
        } catch {
            return null
        }
    }

    async refreshLease(blobId: string, leaseIdVal: string, ttlSeconds = 30): Promise<{ leaseId: string; expiresAt: string; resourceId?: string; mode?: number } | null> {
        try {
            const res = await this.request('PUT',
                `/blobs/${encodeURIComponent(blobId)}/lease/${encodeURIComponent(leaseIdVal)}`,
                this.headers({ 'content-type': 'application/json' }),
                { ttlSeconds }
            )
            if (!res.ok) {
                try { await cancelResponseBody(res) } finally { this.clearTrackedResponse(res) }
                return null
            }
            const payload = await readResponseBodyWithTimeout(
                res,
                () => res.json() as Promise<{ leaseId: string; expiresAt: string; resourceId?: string; mode?: number }>,
                responseBodyTimeoutMs(),
                `PUT /blobs/${encodeURIComponent(blobId)}/lease/${encodeURIComponent(leaseIdVal)}`,
            )
            this.clearTrackedResponse(res)
            return payload
        } catch {
            return null
        }
    }

    async releaseLease(blobId: string, leaseIdVal: string): Promise<boolean> {
        let res: Response | undefined
        try {
            res = await this.request('DELETE',
                `/blobs/${encodeURIComponent(blobId)}/lease/${encodeURIComponent(leaseIdVal)}`,
                this.headers()
            )
            const ok = res.ok
            if (ok) {
                await settleSuccessResponseBody(res, `DELETE /blobs/${encodeURIComponent(blobId)}/lease/${encodeURIComponent(leaseIdVal)}`)
            } else {
                try { await cancelResponseBody(res) } catch { /* ignore */ }
            }
            return ok
        } catch {
            return false
        } finally {
            if (res != null) this.clearTrackedResponse(res)
        }
    }
}
